class BaseformCustomerReviews extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bfCustomerReviewsReady === 'true') return;
    this.dataset.bfCustomerReviewsReady = 'true';

    this.rail = this.querySelector('[data-bf-cr-rail]');
    this.loadMoreItem = this.querySelector('[data-bf-cr-load-more-item]');
    this.ssrSource = this.querySelector('[data-bf-cr-ssr-source]');
    this.deferredReviews = this.querySelector('[data-bf-cr-deferred-reviews]');
    this.hydrateSsrReviews();
    this.slides = Array.from(this.querySelectorAll('[data-bf-cr-slide]'));
    this.prev = this.querySelector('[data-bf-cr-prev]');
    this.next = this.querySelector('[data-bf-cr-next]');
    this.loadMore = this.querySelector('[data-bf-cr-load-more]');
    this.totalReviews = Math.max(this.slides.length, parseInt(this.dataset.bfCrTotal || String(this.slides.length), 10) || this.slides.length);
    this.initialVisible = Math.max(1, parseInt(this.dataset.bfCrInitial || '6', 10) || 6);
    this.loadBatch = Math.max(1, parseInt(this.dataset.bfCrBatch || '6', 10) || 6);

    this.onRootClick = this.handleRootClick.bind(this);
    this.addEventListener('click', this.onRootClick);

    this.initializeLoadMore();

    if (!this.rail || !this.slides.length) return;

    this.onScroll = this.update.bind(this);
    this.onResize = () => {
      this.update();
      this.schedulePdpReviewPreviews();
    };
    this.onPrev = () => this.scrollByCard(-1);
    this.onNext = () => this.scrollByCard(1);

    this.rail.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    this.prev?.addEventListener('click', this.onPrev);
    this.next?.addEventListener('click', this.onNext);

    requestAnimationFrame(() => {
      this.update();
      this.schedulePdpReviewPreviews();
    });
    if (this.classList.contains('bf-customer-reviews--pdp')) {
      document.fonts?.ready?.then(() => this.schedulePdpReviewPreviews());
    }
  }

  disconnectedCallback() {
    if (this.onRootClick) this.removeEventListener('click', this.onRootClick);
    if (this.onScroll) this.rail?.removeEventListener('scroll', this.onScroll);
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.onPrev) this.prev?.removeEventListener('click', this.onPrev);
    if (this.onNext) this.next?.removeEventListener('click', this.onNext);
    if (this.pdpPreviewFrame) cancelAnimationFrame(this.pdpPreviewFrame);
  }

  handleRootClick(event) {
    if (!(event.target instanceof Element)) return;

    const loadMoreButton = event.target.closest('[data-bf-cr-load-more]');
    if (loadMoreButton && this.contains(loadMoreButton)) {
      event.preventDefault();
      this.loadMoreReviews();
      return;
    }

    const galleryThumb = event.target.closest('[data-bf-cr-gallery-thumb]');
    if (galleryThumb && this.contains(galleryThumb)) {
      event.preventDefault();
      this.selectGalleryPhoto(galleryThumb);
      return;
    }

    const galleryPrev = event.target.closest('[data-bf-cr-gallery-photo-prev]');
    if (galleryPrev && this.contains(galleryPrev)) {
      event.preventDefault();
      this.stepGalleryPhoto(galleryPrev.closest('[data-bf-cr-gallery]'), -1);
      return;
    }

    const galleryNext = event.target.closest('[data-bf-cr-gallery-photo-next]');
    if (galleryNext && this.contains(galleryNext)) {
      event.preventDefault();
      this.stepGalleryPhoto(galleryNext.closest('[data-bf-cr-gallery]'), 1);
      return;
    }

    const writeReviewButton = event.target.closest('[data-bf-cr-write-review]');
    if (writeReviewButton && this.contains(writeReviewButton)) {
      event.preventDefault();
      this.openJudgeMeReviewForm(writeReviewButton);
      return;
    }

    const opener = event.target.closest('[data-bf-cr-gallery-open]');
    if (opener && this.contains(opener)) {
      const dialogId = opener.getAttribute('aria-controls');
      let dialog = dialogId ? document.getElementById(dialogId) : null;
      // Text-only reviews and Judge.me hydrated reviews have no Liquid photo dialog.
      // Give them the same full-review dialog treatment as photo reviews.
      if (!(dialog instanceof HTMLDialogElement)) {
        const slide = opener.closest('[data-bf-cr-slide]');
        if (slide) {
          dialog = this.createPdpReviewDialog(slide);
          if (dialog) opener.setAttribute('aria-controls', dialog.id);
        }
      }

      if (dialog instanceof HTMLDialogElement) {
        event.preventDefault();
        this.activeGalleryOpener = opener;
        if (dialog.showModal) dialog.showModal();
        else dialog.setAttribute('open', '');
        document.documentElement.classList.add('bf-review-gallery-open');
        if (!dialog.dataset.bfCrCloseBound) {
          dialog.dataset.bfCrCloseBound = 'true';
          dialog.addEventListener('close', () => {
            document.documentElement.classList.remove('bf-review-gallery-open');
            this.activeGalleryOpener?.focus?.();
          });
        }
      }
      return;
    }

    const closeButton = event.target.closest('[data-bf-cr-gallery-close]');
    if (closeButton && this.contains(closeButton)) {
      const dialog = closeButton.closest('[data-bf-cr-gallery]');
      if (dialog instanceof HTMLDialogElement) {
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
        document.documentElement.classList.remove('bf-review-gallery-open');
        this.activeGalleryOpener?.focus?.();
      }
      return;
    }

    const dialog = event.target.closest('[data-bf-cr-gallery]');
    if (dialog instanceof HTMLDialogElement && event.target === dialog) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      document.documentElement.classList.remove('bf-review-gallery-open');
      this.activeGalleryOpener?.focus?.();
    }
  }

  hydrateSsrReviews() {
    if (!this.rail || !(this.ssrSource instanceof HTMLTemplateElement)) return;

    const fragment = this.ssrSource.content.cloneNode(true);
    const nativeReviews = Array.from(fragment.querySelectorAll('.jdgm-rev'));
    if (!nativeReviews.length) return;

    const existingIds = new Set(
      Array.from(this.querySelectorAll('[data-bf-cr-review-id]'))
        .map((el) => el.getAttribute('data-bf-cr-review-id'))
        .filter(Boolean)
    );

    const maxReviews = Math.max(
      0,
      Math.min(30, parseInt(this.dataset.bfCrTotal || '30', 10) || 30)
    );

    let currentCount = existingIds.size;

    for (const nativeReview of nativeReviews) {
      if (currentCount >= maxReviews) break;

      const reviewId = nativeReview.dataset.reviewId || '';
      if (!reviewId || existingIds.has(reviewId)) continue;

      const slide = this.buildSlideFromJudgeMe(nativeReview);
      if (!slide) continue;

      if (this.loadMoreItem) this.rail.insertBefore(slide, this.loadMoreItem);
      else this.rail.appendChild(slide);

      existingIds.add(reviewId);
      currentCount += 1;
    }
  }

  buildSlideFromJudgeMe(nativeReview) {
    const reviewId =
      nativeReview.dataset.reviewId ||
      nativeReview.getAttribute('data-review-id') ||
      nativeReview.getAttribute('data-review-uuid') ||
      '';
    if (!reviewId) return null;

    const ratingNode =
      nativeReview.querySelector('.jdgm-rev__rating') ||
      nativeReview.querySelector('[data-score]') ||
      nativeReview.querySelector('[aria-label*="star" i]');

    const scoreFromLabel =
      ratingNode?.getAttribute('aria-label')?.match(/([1-5](?:\.\d+)?)\s*(?:out of\s*5\s*)?star/i)?.[1] ||
      '0';

    const rating = Math.max(
      0,
      Math.min(
        5,
        Math.round(
          parseFloat(
            ratingNode?.dataset?.score ||
            ratingNode?.getAttribute('data-rating') ||
            scoreFromLabel ||
            '0'
          ) || 0
        )
      )
    );

    const reviewer = (
      nativeReview.querySelector('.jdgm-rev__author, [data-testid*="author"], [class*="reviewer-name"], [class*="author"]')?.textContent ||
      'Anonymous'
    ).trim();

    const title = (
      nativeReview.querySelector('.jdgm-rev__title, [data-testid*="review-title"], [class*="review-title"]')?.textContent ||
      ''
    ).trim();

    const body = (
      nativeReview.querySelector('.jdgm-rev__body, [data-testid*="review-body"], [class*="review-body"], [class*="review-content"] p')?.textContent ||
      ''
    ).trim();

    const productTitle = (
      nativeReview.dataset.productTitle ||
      nativeReview.getAttribute('data-product-title') ||
      ''
    ).trim();

    const verified =
      nativeReview.dataset.verifiedBuyer === 'true' ||
      nativeReview.getAttribute('data-verified-buyer') === 'true' ||
      Boolean(nativeReview.querySelector('.jdgm-rev__buyer-badge, [class*="verified"]'));

    const time = nativeReview.querySelector('.jdgm-rev__timestamp, time, [data-testid*="date"]');
    const dateValue =
      time?.getAttribute('datetime') ||
      time?.dataset?.content ||
      time?.textContent?.trim() ||
      '';
    const formattedDate = dateValue ? this.formatReviewDate(dateValue) : '';

    const imageUrls = Array.from(
      nativeReview.querySelectorAll(
        '.jdgm-rev__pic-link, a[href*="review-images.judgeme.com"], img[src*="review-images.judgeme.com"], img[data-src*="review-images.judgeme.com"]'
      )
    )
      .map((node) =>
        node.getAttribute('data-mfp-src') ||
        node.getAttribute('href') ||
        node.getAttribute('src') ||
        node.getAttribute('data-src')
      )
      .filter(Boolean)
      .filter((url, index, urls) => urls.indexOf(url) === index);

    const li = document.createElement('li');
    li.className = 'bf-customer-reviews__slide';
    li.dataset.bfCrSlide = '';
    li.dataset.bfCrReviewId = reviewId;

    const card = document.createElement('article');
    card.className = 'bf-customer-reviews__card' + (imageUrls.length ? '' : ' bf-customer-reviews__card--text-only');

    const media = document.createElement('div');
    media.className = imageUrls.length
      ? 'bf-customer-reviews__media'
      : 'bf-customer-reviews__media bf-customer-reviews__media--placeholder';

    if (imageUrls.length) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'bf-customer-reviews__media-button';
      button.dataset.bfCrGalleryOpen = '';
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('aria-label', `View customer review photo from ${reviewer}`);

      const img = document.createElement('img');
      img.src = imageUrls[0];
      img.alt = reviewer.toLowerCase() === 'anonymous'
        ? 'Customer review photo'
        : `Customer review photo from ${reviewer}`;
      img.loading = 'lazy';
      img.width = 900;
      img.height = 675;

      button.appendChild(img);
      media.appendChild(button);
    } else {
      const mark = document.createElement('span');
      mark.className = 'bf-customer-reviews__placeholder-mark';
      mark.setAttribute('aria-hidden', 'true');
      mark.textContent = '“';
      media.appendChild(mark);
    }

    const content = document.createElement('div');
    content.className = 'bf-customer-reviews__body';

    const ratingRow = document.createElement('div');
    ratingRow.className = 'bf-customer-reviews__rating-row';

    const starPill = document.createElement('div');
    starPill.className = 'bf-customer-reviews__star-pill';
    starPill.setAttribute('role', 'img');
    starPill.setAttribute('aria-label', `${rating} out of 5 stars`);

    const stars = document.createElement('span');
    stars.className = 'bf-customer-reviews__stars';
    stars.setAttribute('aria-hidden', 'true');
    stars.textContent = '★'.repeat(rating) + '☆'.repeat(5 - rating);
    starPill.appendChild(stars);
    ratingRow.appendChild(starPill);

    if (formattedDate) {
      const date = document.createElement('div');
      date.className = 'bf-customer-reviews__date';
      date.textContent = formattedDate;
      ratingRow.appendChild(date);
    }

    content.appendChild(ratingRow);

    if (title) {
      const titleEl = document.createElement('div');
      titleEl.className = 'bf-customer-reviews__review-title';
      titleEl.textContent = title;
      content.appendChild(titleEl);
    }

    if (body) {
      const quote = document.createElement('div');
      quote.className = 'bf-customer-reviews__quote';
      quote.textContent = body;
      content.appendChild(quote);
    }

    const meta = document.createElement('div');
    meta.className = 'bf-customer-reviews__meta';

    const reviewerRow = document.createElement('div');
    reviewerRow.className = 'bf-customer-reviews__reviewer-row';

    const reviewerEl = document.createElement('span');
    reviewerEl.className = 'bf-customer-reviews__reviewer';
    reviewerEl.textContent = reviewer;
    reviewerRow.appendChild(reviewerEl);

    if (verified) {
      const verifiedEl = document.createElement('span');
      verifiedEl.className = 'bf-customer-reviews__verified';
      verifiedEl.setAttribute('aria-label', 'Verified buyer');
      verifiedEl.innerHTML = '<span class="bf-customer-reviews__verified-icon" aria-hidden="true"><svg viewBox="0 0 20 20"><circle cx="10" cy="10" r="9" fill="currentColor"/><path d="m6.2 10.2 2.3 2.3 5.2-5.3" fill="none" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></span><span class="bf-customer-reviews__verified-label">Verified buyer</span>';
      reviewerRow.appendChild(verifiedEl);
    }

    meta.appendChild(reviewerRow);

    if (productTitle) {
      const product = document.createElement('div');
      product.className = 'bf-customer-reviews__product';
      product.innerHTML = '<svg class="bf-customer-reviews__product-icon" viewBox="0 0 20 20" aria-hidden="true"><path d="M5.25 6.75h9.5l-.7 9H5.95l-.7-9Z" fill="none" stroke="currentColor" stroke-width="1.35" stroke-linejoin="round"/><path d="M7.5 7V5.75a2.5 2.5 0 0 1 5 0V7" fill="none" stroke="currentColor" stroke-width="1.35" stroke-linecap="round"/></svg>';
      const label = document.createElement('span');
      label.textContent = productTitle;
      product.appendChild(label);
      meta.appendChild(product);
    }

    content.appendChild(meta);
    card.appendChild(media);
    card.appendChild(content);
    li.appendChild(card);
    this.ensurePdpReviewCardOpener(li);

    return li;
  }


  // PDP only: clamp long excerpts while preserving the original text in the DOM.
  // Re-run after responsive changes and progressive Judge.me review loading.
  schedulePdpReviewPreviews() {
    if (!this.classList.contains('bf-customer-reviews--pdp') || !this.rail) return;
    if (this.pdpPreviewFrame) cancelAnimationFrame(this.pdpPreviewFrame);
    this.pdpPreviewFrame = requestAnimationFrame(() => {
      this.pdpPreviewFrame = 0;
      this.updatePdpReviewPreviews();
    });
  }

  // Every PDP review is openable from anywhere on its card, even without an image.
  // A real transparent button provides keyboard and screen-reader access without
  // making the existing image and Read full review buttons nested interactive controls.
  ensurePdpReviewCardOpener(slide) {
    if (!this.classList.contains('bf-customer-reviews--pdp')) return;
    const card = slide.querySelector('.bf-customer-reviews__card');
    if (!card || card.querySelector('[data-bf-cr-card-open]')) return;

    const opener = document.createElement('button');
    opener.type = 'button';
    opener.className = 'bf-customer-reviews__card-open';
    opener.dataset.bfCrCardOpen = '';
    opener.dataset.bfCrGalleryOpen = '';
    opener.setAttribute('aria-haspopup', 'dialog');

    const title = card.querySelector('.bf-customer-reviews__review-title')?.textContent?.trim();
    const reviewer = card.querySelector('.bf-customer-reviews__reviewer')?.textContent?.trim() || 'Customer';
    opener.setAttribute('aria-label', title ? 'Read full review: ' + title : 'Read full review by ' + reviewer);

    const existingImageButton = card.querySelector('.bf-customer-reviews__media-button[aria-controls]');
    if (existingImageButton?.getAttribute('aria-controls')) {
      opener.setAttribute('aria-controls', existingImageButton.getAttribute('aria-controls'));
    }
    card.appendChild(opener);
  }

  updatePdpReviewPreviews() {
    for (const slide of this.slides || []) {
      if (slide.hidden) continue;
      const card = slide.querySelector('.bf-customer-reviews__card');
      this.ensurePdpReviewCardOpener(slide);
      const content = card?.querySelector('.bf-customer-reviews__body');
      const quote = content?.querySelector('.bf-customer-reviews__quote');
      const title = content?.querySelector('.bf-customer-reviews__review-title');
      if (!content || (!quote && !title)) continue;

      let readMore = content.querySelector('[data-bf-cr-read-full]');
      if (!readMore) {
        readMore = document.createElement('button');
        readMore.type = 'button';
        readMore.className = 'bf-customer-reviews__read-more';
        readMore.dataset.bfCrReadFull = '';
        readMore.dataset.bfCrGalleryOpen = '';
        readMore.setAttribute('aria-haspopup', 'dialog');
        readMore.textContent = 'Read full review';
        const meta = content.querySelector('.bf-customer-reviews__meta');
        if (meta) content.insertBefore(readMore, meta);
        else content.appendChild(readMore);
      }

      readMore.hidden = true;
      const truncated = [title, quote].some(el =>
        el && el.clientHeight > 0 && el.scrollHeight > el.clientHeight + 2
      );
      readMore.hidden = !truncated;
    }
  }

  // Reuse the existing photo-gallery dialog styling for text-only reviews.
  // Hydrated Judge.me cards can optionally include their original photo as well.
  createPdpReviewDialog(slide) {
    const card = slide.querySelector('.bf-customer-reviews__card');
    if (!card) return null;
    const cached = slide.dataset.bfCrDetailsId;
    if (cached) {
      const existing = document.getElementById(cached);
      if (existing instanceof HTMLDialogElement) return existing;
    }

    const reviewer = card.querySelector('.bf-customer-reviews__reviewer')?.textContent?.trim() || 'Customer';
    const title = card.querySelector('.bf-customer-reviews__review-title')?.textContent?.trim() || '';
    const body = card.querySelector('.bf-customer-reviews__quote')?.textContent?.trim() || '';
    const stars = card.querySelector('.bf-customer-reviews__stars')?.textContent?.trim() || '';
    const date = card.querySelector('.bf-customer-reviews__date')?.textContent?.trim() || '';
    const product = card.querySelector('.bf-customer-reviews__product span')?.textContent?.trim() || '';
    const photo = card.querySelector('.bf-customer-reviews__media img');
    const verified = Boolean(card.querySelector('.bf-customer-reviews__verified'));
    const id = (this.id || 'BfReview') + '-Detail-' + (++this.reviewDialogCounter || (this.reviewDialogCounter = 1));

    const dialog = document.createElement('dialog');
    dialog.id = id;
    dialog.className = 'bf-customer-reviews__gallery' + (photo ? '' : ' bf-customer-reviews__gallery--text-only');
    dialog.dataset.bfCrGallery = '';
    dialog.setAttribute('aria-labelledby', id + '-title');

    const shell = document.createElement('div');
    shell.className = 'bf-customer-reviews__gallery-shell';
    if (photo) {
      const mediaPane = document.createElement('div');
      mediaPane.className = 'bf-customer-reviews__gallery-media-pane';
      const stage = document.createElement('div');
      stage.className = 'bf-customer-reviews__gallery-stage';
      const bigPhoto = document.createElement('img');
      bigPhoto.className = 'bf-customer-reviews__gallery-main-image';
      bigPhoto.src = photo.currentSrc || photo.src;
      bigPhoto.alt = photo.alt || 'Customer review photo';
      stage.appendChild(bigPhoto);
      mediaPane.appendChild(stage);
      shell.appendChild(mediaPane);
    }

    const pane = document.createElement('aside');
    pane.className = 'bf-customer-reviews__gallery-detail-pane';
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'bf-customer-reviews__gallery-close';
    close.dataset.bfCrGalleryClose = '';
    close.setAttribute('aria-label', 'Close customer review');
    close.innerHTML = '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>';
    pane.appendChild(close);

    const addText = (tagName, className, text, parent = pane) => {
      const el = document.createElement(tagName);
      el.className = className;
      el.textContent = text;
      parent.appendChild(el);
      return el;
    };

    if (product) {
      const productHead = document.createElement('div');
      productHead.className = 'bf-customer-reviews__gallery-product-head';
      const copy = document.createElement('div');
      addText('div', 'bf-customer-reviews__gallery-kicker', 'Review for', copy);
      addText('div', 'bf-customer-reviews__gallery-product-title', product, copy);
      productHead.appendChild(copy);
      pane.appendChild(productHead);
    }

    addText('div', 'bf-customer-reviews__gallery-divider', '');
    const person = document.createElement('div');
    person.className = 'bf-customer-reviews__gallery-reviewer-row';
    addText('span', 'bf-customer-reviews__gallery-avatar', reviewer.slice(0,1).toUpperCase(), person);
    const personCopy = document.createElement('div');
    personCopy.className = 'bf-customer-reviews__gallery-reviewer-copy';
    const nameRow = document.createElement('div');
    nameRow.className = 'bf-customer-reviews__gallery-reviewer-name-row';
    const nameHeading = addText('h3', 'bf-customer-reviews__gallery-reviewer', reviewer, nameRow);
    nameHeading.id = id + '-title';
    if (verified) addText('span', 'bf-customer-reviews__gallery-verified', '✓ Verified buyer', nameRow);
    personCopy.appendChild(nameRow);
    person.appendChild(personCopy);
    pane.appendChild(person);

    const ratingRow = document.createElement('div');
    ratingRow.className = 'bf-customer-reviews__gallery-rating-row';
    if (stars) {
      const starsEl = addText('span', 'bf-customer-reviews__stars', stars, ratingRow);
      const aria = card.querySelector('.bf-customer-reviews__star-pill')?.getAttribute('aria-label');
      if (aria) { starsEl.setAttribute('role', 'img'); starsEl.setAttribute('aria-label', aria); }
    }
    if (date) addText('span', 'bf-customer-reviews__gallery-date', date, ratingRow);
    pane.appendChild(ratingRow);
    if (title) addText('h4', 'bf-customer-reviews__gallery-review-title', title);
    if (body) addText('p', 'bf-customer-reviews__gallery-review-text', body);
    if (verified) {
      const footer = document.createElement('div');
      footer.className = 'bf-customer-reviews__gallery-footer';
      addText('span', 'bf-customer-reviews__gallery-verified', '✓ Verified buyer', footer);
      pane.appendChild(footer);
    }

    shell.appendChild(pane);
    dialog.appendChild(shell);
    this.appendChild(dialog);
    slide.dataset.bfCrDetailsId = id;
    return dialog;
  }

  formatReviewDate(value) {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';

    return parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  initializeLoadMore() {
    if (!this.slides.length) {
      if (this.loadMore) this.loadMore.hidden = true;
      if (this.loadMoreItem) this.loadMoreItem.hidden = true;
      return;
    }

    this.slides.forEach((slide, index) => {
      slide.hidden = index >= this.initialVisible;
    });

    this.updateLoadMore();
  }

  async loadMoreReviews() {
    if (!this.slides.length || this.isLoadingMore) return;

    this.isLoadingMore = true;
    this.setLoadMoreBusy(true);

    try {
      const visibleBefore = this.slides.filter((slide) => !slide.hidden).length;
      const targetVisible = Math.min(this.totalReviews, visibleBefore + this.loadBatch);

      // Reveal any Baseform cards already rendered in the DOM first.
      this.revealReviewsThrough(targetVisible);

      let visibleNow = this.slides.filter((slide) => !slide.hidden).length;

      // Homepage reviews beyond the initial batch live in an inert template.
      // Move only the requested batch into the live DOM when the customer asks for more.
      if (visibleNow < targetVisible && this.deferredReviews instanceof HTMLTemplateElement) {
        this.hydrateDeferredReviews(targetVisible);
        this.slides = Array.from(this.querySelectorAll('[data-bf-cr-slide]'));
        this.revealReviewsThrough(targetVisible);
        visibleNow = this.slides.filter((slide) => !slide.hidden).length;
      }

      // Judge.me's product metafield contains only page 1 on some SKUs.
      // When its reported total is larger, quietly paginate the real hidden
      // Judge.me widget and convert those reviews into our Baseform cards.
      if (visibleNow < targetVisible && this.slides.length < this.totalReviews) {
        await this.hydrateFromNativeJudgeMe(targetVisible);
        this.slides = Array.from(this.querySelectorAll('[data-bf-cr-slide]'));
        this.revealReviewsThrough(targetVisible);
        visibleNow = this.slides.filter((slide) => !slide.hidden).length;
      }

      this.updateLoadMore();

      requestAnimationFrame(() => {
        this.update();
        this.schedulePdpReviewPreviews();

        const firstNew = this.slides[visibleBefore];
        if (firstNew && this.rail) {
          this.rail.scrollTo({
            left: Math.max(0, firstNew.offsetLeft),
            behavior: 'smooth',
          });
        }
      });
    } finally {
      this.isLoadingMore = false;
      this.setLoadMoreBusy(false);
      this.updateLoadMore();
    }
  }

  revealReviewsThrough(targetVisible) {
    this.slides.forEach((slide, index) => {
      if (index < targetVisible) slide.hidden = false;
    });
  }

  setLoadMoreBusy(busy) {
    if (!this.loadMore) return;

    this.loadMore.disabled = busy;
    this.loadMore.setAttribute('aria-busy', busy ? 'true' : 'false');

    const label = this.loadMore.querySelector('span');
    if (label) label.textContent = busy ? 'Loading reviews…' : 'Load more reviews';
  }

  hydrateDeferredReviews(targetCount) {
    if (!(this.deferredReviews instanceof HTMLTemplateElement) || !this.rail) return 0;

    let added = 0;

    while (this.slides.length + added < targetCount) {
      const slide = this.deferredReviews.content.querySelector('[data-bf-cr-slide]');
      if (!slide) break;

      if (this.loadMoreItem) this.rail.insertBefore(slide, this.loadMoreItem);
      else this.rail.appendChild(slide);

      added += 1;
    }

    if (!this.deferredReviews.content.querySelector('[data-bf-cr-slide]')) {
      this.deferredReviews.remove();
      this.deferredReviews = null;
    }

    return added;
  }

  async hydrateFromNativeJudgeMe(targetCount) {
    const source =
      document.querySelector('[data-bf-cr-judgeme-source]') ||
      this.querySelector('[data-bf-cr-judgeme-source]');

    if (!source) return;

    const cappedTarget = Math.min(30, this.totalReviews, targetCount);
    let attempts = 0;
    let stagnantRounds = 0;

    while (this.slides.length < cappedTarget && attempts < 8) {
      attempts += 1;

      const added = this.importNativeJudgeMeReviews(source);
      if (added > 0) {
        this.slides = Array.from(this.querySelectorAll('[data-bf-cr-slide]'));
        stagnantRounds = 0;
        if (this.slides.length >= cappedTarget) break;
      } else {
        stagnantRounds += 1;
      }

      const nextControl = this.findJudgeMeNextControl(source);
      if (!nextControl) {
        // The Judge.me app block can take a moment to initialize.
        if (stagnantRounds <= 2) {
          await this.wait(300);
          continue;
        }
        break;
      }

      const beforeSignature = this.judgeMeReviewSignature(source);
      source.removeAttribute('inert');
      nextControl.click();
      source.setAttribute('inert', '');

      await this.waitForJudgeMeChange(source, beforeSignature, 2400);

      const afterAdded = this.importNativeJudgeMeReviews(source);
      if (afterAdded > 0) {
        this.slides = Array.from(this.querySelectorAll('[data-bf-cr-slide]'));
        stagnantRounds = 0;
      } else {
        stagnantRounds += 1;
      }

      if (stagnantRounds >= 3) break;
    }
  }

  importNativeJudgeMeReviews(source) {
    const existingIds = new Set(
      Array.from(this.querySelectorAll('[data-bf-cr-review-id]'))
        .map((el) => el.getAttribute('data-bf-cr-review-id'))
        .filter(Boolean)
    );

    const nativeReviews = this.getNativeJudgeMeReviews(source);
    let added = 0;

    for (const nativeReview of nativeReviews) {
      if (this.slides.length + added >= Math.min(30, this.totalReviews)) break;

      const reviewId =
        nativeReview.dataset.reviewId ||
        nativeReview.getAttribute('data-review-id') ||
        nativeReview.getAttribute('data-review-uuid') ||
        '';

      if (!reviewId || existingIds.has(reviewId)) continue;

      const slide = this.buildSlideFromJudgeMe(nativeReview);
      if (!slide) continue;

      if (this.loadMoreItem) this.rail.insertBefore(slide, this.loadMoreItem);
      else this.rail.appendChild(slide);

      existingIds.add(reviewId);
      added += 1;
    }

    return added;
  }

  getNativeJudgeMeReviews(source) {
    const legacy = Array.from(
      source.querySelectorAll('.jdgm-rev[data-review-id], .jdgm-rev[data-review-uuid]')
    );
    if (legacy.length) return legacy;

    return Array.from(source.querySelectorAll('[data-review-id], [data-review-uuid]')).filter((el) => {
      if (el.closest('[data-bf-cr-load-more-item]')) return false;

      return Boolean(
        el.querySelector('.jdgm-rev__rating, [data-score], [aria-label*="star" i]') &&
        el.querySelector('.jdgm-rev__author, [class*="author"], [data-testid*="author"]')
      );
    });
  }

  findJudgeMeNextControl(source) {
    const direct = source.querySelector(
      '.jdgm-paginate__next-page:not([disabled]), ' +
      '.jdgm-paginate__page--next:not([disabled]), ' +
      '[data-testid*="pagination-next"]:not([disabled]), ' +
      '[data-testid*="next-page"]:not([disabled]), ' +
      '[aria-label*="next page" i]:not([disabled])'
    );
    if (direct) return direct;

    // Legacy pagination exposes numbered links. Choose the page immediately
    // after the currently active one when a dedicated Next button is absent.
    const numbered = Array.from(source.querySelectorAll('[data-page]'))
      .map((el) => ({
        el,
        page: parseInt(el.getAttribute('data-page') || '', 10),
        current:
          el.classList.contains('jdgm-curt') ||
          el.getAttribute('aria-current') === 'page'
      }))
      .filter((item) => Number.isFinite(item.page));

    if (numbered.length) {
      const current =
        numbered.find((item) => item.current)?.page ||
        Math.min(...numbered.map((item) => item.page));

      const next = numbered
        .filter((item) => item.page > current)
        .sort((a, b) => a.page - b.page)[0];

      if (next?.el) return next.el;
    }

    const candidates = Array.from(source.querySelectorAll('button, a, [role="button"]'));
    return candidates.find((el) => {
      if (el.disabled || el.getAttribute('aria-disabled') === 'true') return false;

      const label = [
        el.textContent,
        el.getAttribute('aria-label'),
        el.getAttribute('title'),
        el.getAttribute('data-testid')
      ]
        .filter(Boolean)
        .join(' ')
        .trim()
        .toLowerCase();

      return (
        label.includes('load more') ||
        label.includes('show more') ||
        label.includes('more reviews') ||
        label === 'next' ||
        label.includes('next page')
      );
    }) || null;
  }

  judgeMeReviewSignature(source) {
    return this.getNativeJudgeMeReviews(source)
      .map((review) =>
        review.dataset.reviewId ||
        review.getAttribute('data-review-id') ||
        review.getAttribute('data-review-uuid') ||
        ''
      )
      .filter(Boolean)
      .join('|');
  }

  async waitForJudgeMeChange(source, previousSignature, timeout = 2400) {
    const started = Date.now();

    while (Date.now() - started < timeout) {
      await this.wait(100);

      const signature = this.judgeMeReviewSignature(source);
      if (signature && signature !== previousSignature) return true;
    }

    return false;
  }

  wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  updateLoadMore() {
    if (!this.loadMore) return;

    const visibleCount = this.slides.filter((slide) => !slide.hidden).length;
    const remaining = Math.max(0, this.totalReviews - visibleCount);
    const hasMore = remaining > 0;

    this.loadMore.hidden = !hasMore;
    if (this.loadMoreItem) this.loadMoreItem.hidden = !hasMore;

    if (!this.isLoadingMore) {
      const label = this.loadMore.querySelector('span');
      if (label) label.textContent = 'Load more reviews';
    }

    this.loadMore.setAttribute(
      'aria-label',
      hasMore ? `Load more reviews. ${remaining} remaining.` : 'All reviews loaded'
    );
  }

  selectGalleryPhoto(thumb) {
    const dialog = thumb.closest('[data-bf-cr-gallery]');
    const main = dialog?.querySelector('[data-bf-cr-gallery-main]');
    if (!dialog || !main) return;

    const url = thumb.dataset.imageUrl;
    const alt = thumb.dataset.imageAlt || '';
    if (url) main.src = url;
    main.alt = alt;

    dialog.querySelectorAll('[data-bf-cr-gallery-thumb]').forEach((item) => {
      const active = item === thumb;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }

  stepGalleryPhoto(dialog, direction) {
    if (!(dialog instanceof Element)) return;
    const thumbs = Array.from(dialog.querySelectorAll('[data-bf-cr-gallery-thumb]'));
    if (!thumbs.length) return;

    let index = thumbs.findIndex((item) => item.classList.contains('is-active'));
    if (index < 0) index = 0;
    index = (index + direction + thumbs.length) % thumbs.length;
    this.selectGalleryPhoto(thumbs[index]);
  }

  openJudgeMeReviewForm(button) {
    const source = this.querySelector('[data-bf-cr-judgeme-source]') || document.querySelector('[data-bf-cr-judgeme-source]');
    let attempts = 0;
    const maxAttempts = 40;

    const findWriteButton = () => {
      if (!source) return null;

      const direct = source.querySelector(
        '.jdgm-write-rev-link, [data-testid*="write-review"], [data-testid*="write_review"], [data-action*="write-review"], [data-action*="write_review"]'
      );
      if (direct) return direct;

      const candidates = source.querySelectorAll('button, a, [role="button"]');
      return Array.from(candidates).find((el) => {
        const label = [
          el.textContent,
          el.getAttribute('aria-label'),
          el.getAttribute('title')
        ].filter(Boolean).join(' ').trim().toLowerCase();

        return label.includes('write a review') || label.includes('write review');
      }) || null;
    };

    const hasVisibleReviewDialog = () => {
      const dialogs = document.querySelectorAll(
        'dialog[open], [role="dialog"], .jdgm-form-wrapper, .jdgm-write-rev-form, [class*="review-form"][class*="modal"]'
      );

      return Array.from(dialogs).some((el) => {
        if (source && source.contains(el)) return false;
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
      });
    };

    const open = () => {
      const nativeButton = findWriteButton();

      if (nativeButton) {
        source?.removeAttribute('inert');
        nativeButton.click();

        window.setTimeout(() => {
          if (hasVisibleReviewDialog()) {
            source?.setAttribute('inert', '');
            return;
          }

          // Legacy Judge.me can expand its form inside the Review Widget instead of
          // creating a body-level modal. Bring that official widget on-screen only
          // in that fallback case.
          const inlineForm = source?.querySelector(
            '.jdgm-form-wrapper, .jdgm-write-rev-form, form[class*="jdgm"], form'
          );

          if (inlineForm) {
            source.classList.add('bf-customer-reviews__judgeme-source--fallback-open');
            source.removeAttribute('aria-hidden');
            source.removeAttribute('inert');
          } else {
            source?.setAttribute('inert', '');
          }
        }, 350);

        return;
      }

      attempts += 1;
      if (attempts < maxAttempts) {
        window.setTimeout(open, 100);
        return;
      }

      // Judge.me controls whether storefront review writing is allowed.
      // If its native action never appears, leave the button enabled and fail gracefully.
      button.dataset.bfReviewUnavailable = 'true';
    };

    open();
  }

  scrollByCard(direction) {
    const card = this.slides[0];
    if (!card) return;

    const styles = getComputedStyle(this.rail);
    const gap = parseFloat(styles.columnGap || styles.gap || 0);
    this.rail.scrollBy({
      left: direction * (card.getBoundingClientRect().width + gap),
      behavior: 'smooth',
    });
  }

  update() {
    if (!this.rail) return;

    const max = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
    const current = Math.max(0, this.rail.scrollLeft);
    const overflow = max > 2;

    if (this.prev) {
      this.prev.hidden = !overflow;
      this.prev.disabled = current <= 2;
    }

    if (this.next) {
      this.next.hidden = !overflow;
      this.next.disabled = current >= max - 2;
    }
  }
}

if (!customElements.get('baseform-customer-reviews')) {
  customElements.define('baseform-customer-reviews', BaseformCustomerReviews);
}
